import { useState, useContext, ChangeEvent } from 'react';
import { IconButton, Typography } from '@mui/material';
import UploadButtonIcon from '../../../assets/UploadButtonIcon.svg';
import { ProfileContext } from '../../../context/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { getApprovalStatus } from '../../../utils/auth-token-utils/cookie';
import CircularProgress from '@mui/material/CircularProgress';

import StyledButton from '../../../components/Button/Button';
import { createProfile } from '../../../services/NetworkServices';
import { Box, Container, FormControl } from '@mui/material';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import './UploadBusinessCard.css';
import ApiStatus from '../../../config/apiStatusConstants';
import { NewProfile } from '../../../context/ProfileContext';
import Popup from '../../../components/Inputs/Popup';

const UploadBusinessCard: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [showPopUp, setShowPopUp] = useState<boolean>(false);

  const profileContext = useContext(ProfileContext);

  if (!profileContext) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }
  const {
    handleNewProfile,
    newProfile,
    handleProfileCreation,
    profileStage,
    handleProfileStage,
  } = profileContext;
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);

  const [errorMsg, setErrorMsg] = useState<string>('');
  const navigate = useNavigate();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, type, files } = event.target;

    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      if (name === 'idFront') {
        setFrontPreview(URL.createObjectURL(file));
      }
      if (name === 'idBack') {
        setBackPreview(URL.createObjectURL(file));
      }
      handleNewProfile({ [name]: file });
    }
  };

  const handleContinue = (): void => {
    const approvalStatus = getApprovalStatus();
    if (approvalStatus && approvalStatus !== 'approved') {
      navigate('/babysitter/waitingForApproval');
    } else {
      navigate('/supervisor/dashboard');
    }
  };

  const handleUpload = async () => {
    try {
      setApiStatus(ApiStatus.LOADING);
      const formData = new FormData();

      console.log('new profile is', newProfile);

      // Loop through the fields in newProfile and append them to formData
      for (const key in newProfile) {
        if (newProfile.hasOwnProperty(key)) {
          const typedKey = key as keyof NewProfile; // Type assertion
          let value = newProfile[typedKey];

          // Check if the key is 'phone' and slice the phone number to remove the '+1'
          if (
            typedKey === 'phone' &&
            typeof value === 'string' &&
            value.startsWith('+1')
          ) {
            value = value.slice(2); // Remove the +1 from the phone number
          }
          console.log('value is ', value);
          if (value !== '') {
            formData.append(typedKey, value);
          }
        }
      }
      console.log('final form Data is ', formData);
      const response = await createProfile(formData);
      console.log('response is', response);
      const data = await response.data;
      if (data) {
        setApiStatus(ApiStatus.SUCCESS);
        handleProfileCreation();
        setShowPopUp(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      console.log('error is ', error);
      setErrorMsg(
        error.response.data.message ||
          'Something went wrong. Please try again.',
      );
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton onClick={handleUpload} disabled={!newProfile.idFront}>
          Continue
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    console.log('error is', errorMsg);
    if (apiStatus === ApiStatus.FAILURE) {
      return <p style={{ color: 'red' }}>{errorMsg}</p>;
    }
    return null;
  };

  const backClick = () => {
    if (profileStage !== 1) {
      handleProfileStage(profileStage - 1);
    } else {
      navigate('/signup');
    }
  };

  return (
    <>
      {showPopUp && <div className="backdrop"></div>} {/* Overlay backdrop */}
      <StyledBgContainer>
        <TopNav showArrow={true} backClick={backClick} />
        <Container maxWidth="sm">
          <Box component="form" noValidate autoComplete="off">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: { sm: 6 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontFamily: 'Opensans',
                  fontWeight: '700',
                }}
              >
                Upload Business Card
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'grey', mt: 1, mb: 3, textAlign: 'center' }}
              >
                Please upload a clear image of your business card to update your
                information
              </Typography>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{
                  gap: 2,
                  mb: 1,
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <Box>
                  <Box
                    className="upload-container"
                    sx={{ width: { xs: '45vw', sm: '35vw', md: '16vw' } }}
                  >
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="upload-front-file"
                      type="file"
                      name="idFront"
                      onChange={handleChange}
                    />
                    {!frontPreview && (
                      <>
                        <label htmlFor="upload-front-file">
                          <IconButton
                            color="primary"
                            aria-label="upload government id"
                            component="span"
                          >
                            <img src={UploadButtonIcon} alt="Upload Icon" />
                          </IconButton>
                        </label>
                        <p style={{ color: 'white' }}>Upload</p>
                      </>
                    )}
                    {frontPreview && (
                      <>
                        <img
                          src={frontPreview}
                          alt="Profile Preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '20px',
                            margin: 'auto',
                            display: 'block',
                          }}
                        />
                      </>
                    )}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Opensans',
                      fontWeight: '400',
                      fontSize: '15px',
                    }}
                  >
                    Front ( Mandatory )
                  </Typography>
                </Box>
                <Box>
                  <Box
                    className="upload-container"
                    sx={{ width: { xs: '45vw', sm: '35vw', md: '16vw' } }}
                  >
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="upload-back-file"
                      type="file"
                      name="idBack"
                      onChange={handleChange}
                    />
                    {!backPreview && (
                      <>
                        <label htmlFor="upload-back-file">
                          <IconButton
                            color="primary"
                            aria-label="upload picture"
                            component="span"
                          >
                            <img src={UploadButtonIcon} alt="Upload Icon" />
                          </IconButton>
                        </label>
                        <p style={{ color: 'white' }}>Upload</p>
                      </>
                    )}
                    {backPreview && (
                      <img
                        src={backPreview}
                        alt="Profile Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '20px',
                          margin: 'auto',
                          display: 'block',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Opensans',
                      fontWeight: '400',
                      fontSize: '15px',
                    }}
                  >
                    Back ( Optional )
                  </Typography>
                </Box>
              </FormControl>

              {renderErrorMsg()}
              <Box sx={{ mt: 5, mb: 8 }}> {renderButton()}</Box>
              {showPopUp && (
                <Popup
                  title=<Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'opensans',
                      fontWeight: '700',
                      lineHeight: '1.4',
                    }}
                  >
                    {' '}
                    Profile Created <br /> Successfully{' '}
                  </Typography>
                  message=<Typography variant="body2">
                    {' '}
                    Your profile has been created! Thank you for joining{' '}
                  </Typography>
                  buttonText="Done"
                  onButtonClick={handleContinue}
                />
              )}
            </Box>
          </Box>
        </Container>
      </StyledBgContainer>
    </>
  );
};

export default UploadBusinessCard;
